// First Goal — half-donut widget (Phase 3 → M2 wave 6). Thin wrapper over
// <HalfDonut>.
//
// One donut per fixture team. Resolves each team's shaped donut (segments +
// centre % + W/D/L stats) from the split/timeline/view controls and delegates
// the drawing. Keyed by fixture so a fixture change remounts the donuts (arcs
// snap in, matching the legacy innerHTML rebuild); split/view/timeline changes
// keep the mount so the arcs animate. Data now comes from app-api via
// `useWidgetData` (was the `getFirstGoalData` legacyData getter); the shared
// shaper + delegated DOM are unchanged. Controls reshape client-side, no refetch.

import { shapeFirstGoalDonut, type FirstGoalSplit, type FirstGoalView } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import HalfDonut, { type DonutConfig } from "./HalfDonut.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface FirstGoalProps {
  fixture: string;
  split?: string | null;
  timeline?: string | null;
  view?: string | null;
}

export default function FirstGoal({
  fixture,
  split,
  timeline,
  view,
}: FirstGoalProps) {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const teams = t2 ? [t1, t2] : [t1];

  const splitVal: FirstGoalSplit = split === "CF" ? "CF" : "SF";
  const viewVal: FirstGoalView = view === "Frequency" ? "Frequency" : "Outcome";
  const { data, isPending, isError, refetch } = useWidgetData(
    "first-goal",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const donuts: DonutConfig[] = teams.map((team) => {
    const shaped = shapeFirstGoalDonut(data[team], splitVal, timeline, viewVal);
    return {
      idNs: team.replace(/\s+/g, ""),
      dataValue: team,
      label: team,
      segments: shaped.segments,
      pct: shaped.pct,
      statsSpans: shaped.statsSpans,
    };
  });

  return (
    <HalfDonut
      key={fixture}
      dataAttr="team"
      idPrefix="fg"
      hasStats
      donuts={donuts}
    />
  );
}
